import {
	stepUpTokenResponse,
	webauthnLoginBody,
	webauthnLoginOptions,
	webauthnRegistrationBody,
	webauthnRegistrationOptions,
} from "~/backend/validate.js";

import { parse } from "~/util/validate.js";

import { apiRequest, authedApiRequest, response, ApiError } from "./api.js";

/**
 * @typedef {"email_update"
 * 	| "password_update"
 * 	| "tfa_confirm"
 * 	| "tfa_delete"
 * 	| "passkey_register"
 * 	| "passkey_delete"} StepUpAction
 */

/**
 * Converts a URL-encoded base64 string to an ArrayBuffer.
 *
 * @param {string} b64url
 * @returns {ArrayBuffer}
 */
function fromBase64Url(b64url) {
	const b64 = b64url
		.replace(/-/g, "+")
		.replace(/_/g, "/")
		.padEnd(b64url.length + ((4 - (b64url.length % 4)) % 4), "=");

	const binary = atob(b64);
	const bytes = new Uint8Array(binary.length);
	for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

	return bytes.buffer;
}

/**
 * Converts an ArrayBuffer to a URL-encoded base64 string.
 *
 * @param {ArrayBuffer} buffer
 * @returns {string}
 */
function toBase64Url(buffer) {
	return btoa(String.fromCharCode(...new Uint8Array(buffer)))
		.replace(/\+/g, "-")
		.replace(/\//g, "_")
		.replace(/=/g, "");
}

export class PasskeyError extends ApiError {}

/**
 * @param {webauthnRegistrationOptions["publicKey"]} publicKey
 * @returns {Promise<Omit<webauthnRegistrationBody, "token" | "password"> | null>}
 */
let createCredential = async publicKey => {
	const { challenge, user, excludeCredentials, ...rest } = publicKey;

	const credential = await navigator.credentials.create({
		publicKey: /** @type {PublicKeyCredentialCreationOptions} */ ({
			...rest,
			challenge: fromBase64Url(challenge),
			user: { ...user, id: fromBase64Url(user.id) },
			...(excludeCredentials && {
				excludeCredentials: excludeCredentials.map(c => ({ ...c, id: fromBase64Url(c.id) })),
			}),
		}),
	});
	if (!(credential instanceof PublicKeyCredential)) return null;

	const resp = /** @type {AuthenticatorAttestationResponse} */ (credential.response);
	return {
		id: credential.id,
		type: credential.type,
		rawId: toBase64Url(credential.rawId),
		authenticatorAttachment: credential.authenticatorAttachment ?? undefined,
		clientExtensionResults: /** @type {{ [key: string]: string }} */ (
			credential.getClientExtensionResults()
		),
		response: {
			attestationObject: toBase64Url(resp.attestationObject),
			authenticatorData: toBase64Url(resp.getAuthenticatorData()),
			clientDataJSON: toBase64Url(resp.clientDataJSON),
			publicKey: toBase64Url(resp.getPublicKey() ?? new ArrayBuffer()),
			publicKeyAlgorithm: resp.getPublicKeyAlgorithm(),
			transports: resp.getTransports(),
		},
	};
};

/**
 * @param {webauthnLoginOptions["publicKey"]} publicKey
 * @returns {Promise<Omit<webauthnLoginBody, "machine_id"> | null>}
 */
let getCredential = async publicKey => {
	const { challenge, allowCredentials, ...rest } = publicKey;

	const credential = await navigator.credentials.get({
		publicKey: /** @type {PublicKeyCredentialRequestOptions} */ ({
			...rest,
			challenge: fromBase64Url(challenge),
			...(allowCredentials && {
				allowCredentials: allowCredentials.map(c => ({ ...c, id: fromBase64Url(c.id) })),
			}),
		}),
	});
	if (!(credential instanceof PublicKeyCredential)) return null;

	const assertion = /** @type {AuthenticatorAssertionResponse} */ (credential.response);
	return {
		id: credential.id,
		type: credential.type,
		rawId: toBase64Url(credential.rawId),
		authenticatorAttachment: credential.authenticatorAttachment ?? undefined,
		clientExtensionResults: /** @type {{ [key: string]: string }} */ (
			credential.getClientExtensionResults()
		),
		response: {
			authenticatorData: toBase64Url(assertion.authenticatorData),
			clientDataJSON: toBase64Url(assertion.clientDataJSON),
			signature: toBase64Url(assertion.signature),
			userHandle: toBase64Url(assertion.userHandle ?? new ArrayBuffer()),
		},
	};
};

/**
 * Overrides the functions used to create/get passkeys.
 *
 * @param {(publicKey: unknown) => Promise<unknown>} create
 * @param {(publicKey: unknown) => Promise<unknown>} get
 */
export function setCredentialFunctions(create, get) {
	createCredential = async publicKey => {
		const credential = await create(publicKey);
		return credential ? parse(webauthnRegistrationBody, credential) : null;
	};

	getCredential = async publicKey => {
		const credential = await get(publicKey);
		return credential ? parse(webauthnLoginBody, credential) : null;
	};
}

/**
 * Registers a new passkey.
 *
 * @param {string} token - A step-up authentication token.
 */
export async function register(token) {
	const { publicKey } = (
		await authedApiRequest("POST", "/v2/passkeys/registration/begin", {
			schema: response(webauthnRegistrationOptions),
		})
	).data;

	let credential;
	try {
		credential = await createCredential(publicKey);
	} catch (e) {
		if (!(e instanceof Error)) throw e;
		throw new PasskeyError(-1, [{ code: "passkey_error", message: e.message }]);
	}

	if (!credential) {
		throw new PasskeyError(-1, [{ code: "no_credential", message: "Couldn't create credential." }]);
	}

	return await authedApiRequest("POST", "/v2/passkeys/registration/finish", {
		json: { ...credential, token },
	});
}

/**
 * Performs step-up authentication using a passkey.
 *
 * @param {StepUpAction} action - The step-up action to authorize.
 * @returns {Promise<string>} The single-use step-up token.
 */
export async function stepUp(action) {
	const { publicKey } = (
		await authedApiRequest("POST", "/v2/auth/step-up/passkey/begin", {
			schema: response(webauthnLoginOptions),
		})
	).data;

	let credential;
	try {
		credential = await getCredential(publicKey);
	} catch (e) {
		if (!(e instanceof Error)) throw e;
		throw new PasskeyError(-1, [{ code: "passkey_error", message: e.message }]);
	}

	if (!credential) {
		throw new PasskeyError(-1, [{ code: "no_credential", message: "Couldn't get credential." }]);
	}

	const { data } = await authedApiRequest("POST", "/v2/auth/step-up/passkey/finish", {
		json: { action, ...credential },
		schema: response(stepUpTokenResponse),
	});

	return data.token;
}

/**
 * Prompts for a passkey and returns the assertion needed to finish authentication.
 *
 * @returns {Promise<Omit<webauthnLoginBody, "machine_id">>}
 */
export async function getPasskeyCredential() {
	const { publicKey } = (
		await apiRequest("POST", "/v2/auth/webauthn/begin", {
			schema: response(webauthnLoginOptions),
		})
	).data;

	let credential;
	try {
		credential = await getCredential(publicKey);
	} catch (e) {
		if (!(e instanceof Error)) throw e;
		throw new PasskeyError(-1, [{ code: "passkey_error", message: e.message }]);
	}

	if (!credential) {
		throw new PasskeyError(-1, [{ code: "no_credential", message: "Couldn't get credential." }]);
	}

	return credential;
}
